import { z } from 'zod';
import { PricingFeatures } from '../../types/organizationGroupFeatures';

const softLimitSchema = z
  .object({
    ignore: z.boolean(),
    used: z.number(),
    limit: z.number(),
  })
  .catch({
    ignore: true,
    used: 0,
    limit: 0,
  });

const getHardLimitSchema = (fallbackValue: boolean) =>
  z.object({ purchased: z.boolean() }).catch({
    purchased: fallbackValue,
  });

export const getSubscriptionUsageSchema = <
  T extends PricingFeatures | undefined,
>(
  features: T
) =>
  z
    .object({
      queues: softLimitSchema,
      documentArchive: softLimitSchema,
      sandboxes: softLimitSchema,
      extensions: getHardLimitSchema(false),
      usageReports: getHardLimitSchema(
        Boolean(features?.usageReports?.enabled)
      ),
      automationAssistant: getHardLimitSchema(
        Boolean(features?.automationAssistant?.enabled)
      ),
      embeddedMode: getHardLimitSchema(false),
      labels: getHardLimitSchema(Boolean(features?.labels?.enabled)),
      extensionLogs: getHardLimitSchema(false),
      duplicateHandling: getHardLimitSchema(false),
      intelligentMailbox: getHardLimitSchema(false),
      attachments: getHardLimitSchema(Boolean(features?.attachments?.enabled)),
      documentRemoval: getHardLimitSchema(
        Boolean(features?.documentRemoval?.enabled)
      ),
      approvalWorkflows: getHardLimitSchema(
        Boolean(features?.approvalWorkflows?.enabled)
      ),
      formulaFields: getHardLimitSchema(
        Boolean(features?.formulaFields?.enabled)
      ),
      documentTranslations: getHardLimitSchema(
        Boolean(features?.documentTranslations?.enabled)
      ),
      einvoicing: getHardLimitSchema(Boolean(features?.einvoicing?.enabled)),
      einvoicingGermany: getHardLimitSchema(
        Boolean(features?.einvoicingGermany?.enabled)
      ),
      dataCaptureLock: getHardLimitSchema(
        Boolean(features?.dataCaptureLock?.enabled)
      ),
    })
    .transform(({ einvoicingGermany, einvoicing, ...data }) => {
      return {
        ...data,
        einvoicing: {
          purchased: einvoicingGermany.purchased || einvoicing.purchased,
        },
      };
    });

type SubscriptionUsageZodSchema = ReturnType<typeof getSubscriptionUsageSchema>;

// mimics exactly what comes from BE
export type SuscriptionUsageInput = z.input<SubscriptionUsageZodSchema>;

type SubscriptionUsageOutput = z.TypeOf<SubscriptionUsageZodSchema>;

// When pricing is not active for some reason BE sends an empty usage object.
// In this case in FE we add `hasSubscriptionPlan` boolean to our state to be able to handle some components that should behave differently when there is no subscription plan.
export type ActiveSubscriptionUsage = SubscriptionUsageOutput & {
  hasSubscriptionPlan: boolean;
};
export type FeatureKey = keyof SubscriptionUsageOutput;

export type HardLimitKeys = {
  [K in FeatureKey]: 'purchased' extends keyof SubscriptionUsageOutput[K]
    ? K
    : never;
}[FeatureKey];

export type SoftLimitKeys = Exclude<FeatureKey, HardLimitKeys>;
export type SoftLimit = z.TypeOf<typeof softLimitSchema>;
